import React, { useEffect, useMemo, useRef, useState } from "react";

export default function TLCBanner({
  items = [],                 // [{ alt, src, srcSet, dataId, href }]
  autoplay = true,
  autoplayMs = 3000,
  className = "",
  slideWidth = 1905,          // ✅ ให้ตรงกับเว็บที่คุณสแกน
  speedMs = 2000,             // ✅ transition-duration แบบ swiper
  dupCount = 3,               // ✅ จำนวน duplicate หน้า/หลัง
}) {
  const len = items.length;

  // index จริง (0..len-1)
  const [index, setIndex] = useState(0);

  // index ภายใน loopItems (รวม duplicate)
  const [loopIndex, setLoopIndex] = useState(0);

  // เปิด/ปิด transition (ไว้ snap กลับแบบ 0ms)
  const [withTransition, setWithTransition] = useState(true);

  const timerRef = useRef(null);

  // ----- build loop items -----
  const { loopItems, headDupLen, tailDupLen } = useMemo(() => {
    if (len <= 1) return { loopItems: items, headDupLen: 0, tailDupLen: 0 };

    const n = Math.min(dupCount, len);
    const headDup = items.slice(0, n);
    const tailDup = items.slice(-n);

    return {
      loopItems: [...tailDup, ...items, ...headDup],
      headDupLen: n,
      tailDupLen: n,
    };
  }, [items, len, dupCount]);

  const minReal = tailDupLen;                // จุดเริ่มของ “ของจริง”
  const maxReal = tailDupLen + len - 1;      // จุดท้ายของ “ของจริง”

  // init loopIndex ให้อยู่ตรงของจริง
  useEffect(() => {
    if (len <= 1) {
      setIndex(0);
      setLoopIndex(0);
      return;
    }
    setIndex(0);
    setLoopIndex(tailDupLen); // ชี้ตัวแรกของจริง
  }, [len, tailDupLen]);

  // ----- helpers -----
  const goToReal = (realI) => {
    if (!len) return;
    const nextReal = ((realI % len) + len) % len;
    setIndex(nextReal);

    // เราเลื่อนด้วย loopIndex ที่ชี้ "ของจริง"
    setWithTransition(true);
    setLoopIndex(tailDupLen + nextReal);
  };

  const next = () => goToReal(index + 1);
  const prev = () => goToReal(index - 1);

  // autoplay (ไม่ reset interval ทุกครั้ง)
  useEffect(() => {
    if (!autoplay || len <= 1) return;
    const t = setInterval(() => {
      setWithTransition(true);
      setIndex((v) => {
        const nv = (v + 1) % len;
        setLoopIndex(tailDupLen + nv);
        return nv;
      });
    }, autoplayMs);

    return () => clearInterval(t);
  }, [autoplay, autoplayMs, len, tailDupLen]);

  // ----- snap-back logic (เหมือน Swiper loop) -----
  // เมื่อ transition จบ ถ้าอยู่ใน duplicate ให้ snap กลับไปตำแหน่งของจริง “แบบ 0ms”
  const onTransitionEnd = () => {
    if (len <= 1) return;

    // ถ้าเลยขวาสุด (เข้า head duplicates)
    if (loopIndex > maxReal) {
      const overflow = loopIndex - (maxReal + 1); // 0..headDupLen-1
      const newLoop = minReal + overflow;         // กลับไปของจริง
      setWithTransition(false);
      setLoopIndex(newLoop);
      setIndex(overflow); // real index
      // เปิด transition กลับคืนใน tick ถัดไป
      requestAnimationFrame(() => requestAnimationFrame(() => setWithTransition(true)));
      return;
    }

    // ถ้าเลยซ้ายสุด (เข้า tail duplicates)
    if (loopIndex < minReal) {
      const under = minReal - loopIndex;          // 1..tailDupLen
      const newReal = len - under;                // real index ปลาย ๆ
      const newLoop = tailDupLen + newReal;       // ของจริง
      setWithTransition(false);
      setLoopIndex(newLoop);
      setIndex(newReal);
      requestAnimationFrame(() => requestAnimationFrame(() => setWithTransition(true)));
    }
  };

  // ----- wrapper style: translate3d -----
  const wrapperStyle = useMemo(() => {
    const duration = withTransition ? `${speedMs}ms` : "0ms";
    return {
      transitionDuration: duration,
      transform: `translate3d(-${loopIndex * slideWidth}px, 0px, 0px)`,
      willChange: "transform",
      display: "flex",
    };
  }, [withTransition, speedMs, loopIndex, slideWidth]);

  // ----- render -----
  if (!len) {
    return (
      <div className={`index-banner ${className}`}>
        <div className="web_banner">
          <div className="banner-wrapper" data_vue_tag="Banner">
            <div className="swiper-container">
              <div className="swiper-wrapper" style={{ display: "flex" }}>
                <div className="web swiper-slide swiper-slide-active" style={{ width: slideWidth }}>
                  <div className="banner-content">กำลังโหลด…</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`index-banner ${className}`}>
      <div className="web_banner">
        <div className="banner-wrapper" data_vue_tag="Banner">
          <div className="swiper-container swiper-container-initialized swiper-container-horizontal">
            <div
              className="swiper-wrapper"
              style={wrapperStyle}
              onTransitionEnd={onTransitionEnd}
            >
              {loopItems.map((it, loopI) => {
                const realIndex =
                  len > 0 ? ((loopI - tailDupLen) % len + len) % len : 0;

                const isActive = loopI === loopIndex;
                const isPrev = loopI === loopIndex - 1;
                const isNext = loopI === loopIndex + 1;

                const isDuplicate =
                  len > 1 &&
                  (loopI < tailDupLen || loopI >= tailDupLen + len);

                const slideClass = [
                  "web",
                  "swiper-slide",
                  isDuplicate ? "swiper-slide-duplicate" : "",
                  isPrev ? "swiper-slide-prev" : "",
                  isActive ? "swiper-slide-active" : "",
                  isNext ? "swiper-slide-next" : "",
                ]
                  .filter(Boolean)
                  .join(" ");

                return (
                  <div
                    key={`slide-${loopI}`}
                    className={slideClass}
                    data-swiper-slide-index={realIndex}
                    style={{ width: slideWidth, flex: "0 0 auto" }}
                  >
                    <div className="banner-content">
                      {it?.href ? (
                        <a href={it.href}>
                          <img
                            data-id={it?.dataId}
                            alt={it?.alt || ""}
                            className="swiper-lazy swiper-lazy-loaded"
                            srcSet={it?.srcSet}
                            src={it?.src}
                          />
                        </a>
                      ) : (
                        <img
                          data-id={it?.dataId}
                          alt={it?.alt || ""}
                          className="swiper-lazy swiper-lazy-loaded"
                          srcSet={it?.srcSet}
                          src={it?.src}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* pagination */}
            {len > 1 && (
              <div className="swiper-pagination swiper-pagination-clickable swiper-pagination-bullets swiper-pagination-bullets-dynamic">
                {items.map((_, i) => (
                  <span
                    key={`dot-${i}`}
                    className={`swiper-pagination-bullet${index === i ? " swiper-pagination-bullet-active swiper-pagination-bullet-active-main" : ""}`}
                    tabIndex={0}
                    role="button"
                    aria-label={`Go to slide ${i + 1}`}
                    onClick={() => goToReal(i)}
                  />
                ))}
              </div>
            )}

            <span className="swiper-notification" aria-live="assertive" aria-atomic="true" />
          </div>
        </div>
      </div>
    </div>
  );
}
